/*
 * Copyright (c) 2021 OTB Africa <https://otbafrica.com>
 *
 * Created Date: Friday, October 15th 2021, 12:02:54 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { useGlobalScope } from "core/providers";
import { QueryParameters } from "lib/types";
import React from "react";

/**
 *
 * This hook simply returns dependencies that should trigger a refetch of the request. These dependencies are:
 *    1. OrganizationParams: Also appends an organization filter to the API Request parameters
 *    2. loggedInUser: This is used to trigger a refetch when the user logs in again after a session expires.
 *
 * @param filterByGlobalOrganization Whether or not we should filter the request by organization selected in global scope. Note: This should always be true unless you are fetching data that does not have organization set.
 * @returns
 *    1. parameters updated with organization filter paramater i.e. ?organization=xxx
 *    2. loggedInUser object. Even if the object data does not change, the object reference will change when the user logs in again after a session expires. And
 *      this is what we want to trigger a refetch.
 */

type IReturn<QT> = {
  organizationQueryParams: Pick<QueryParameters<QT>, "organization">;
  loggedInUser: any;
};

export const useAPIRequestDependencies = <QT>(filterByGlobalOrganization: boolean): IReturn<QT> => {
  const { globalState, userContext } = useGlobalScope();

  const selectedOrganizationId = globalState?.selectedOrganization?.id ?? ""; // null coalescing for global state because our global context provider actually makes API calls before setting the context i.e. calling the organizations and user end points.

  const organizationQueryParams = React.useMemo(
    () => (filterByGlobalOrganization ? { organization: selectedOrganizationId } : {}),
    [filterByGlobalOrganization, selectedOrganizationId]
  );

  const loggedInUser = userContext?.loggedInUser;

  return { organizationQueryParams, loggedInUser };
};
